import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../context/AppContext";
import { hapticSelect } from "../lib/haptics";
import { Reply, Copy, Download, Forward, Trash2, X, Star, StarOff, Pin, Pencil, CheckSquare, SmilePlus, Info, Sticker, Link2, Unlink } from "lucide-react";

const QUICK_REACTIONS = ["\u{1F44D}", "\u{2705}", "\u{1F44C}", "\u{1F6A8}", "\u{2753}"];

/**
 * Bottom action sheet opened by long-pressing (or right-clicking) any message:
 * quick reactions + Reply / Copy / Save / Forward / Delete.
 * Custom portal implementation (reliable positioning on all devices).
 */
export function MessageActionsSheet({
  open, onClose, canCopy, canSave, canDelete, isStarred,
  onReact, onMoreEmoji, onReply, onCopy, onSave, onForward, onDelete, onStar, onPin, onEdit, saveLabel, onSelect, onInfo, onMakeSticker,
  onLinkJob, linkLabel, onUnlink, unlinkLabel,
}) {
  const { t } = useApp();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const Row = ({ icon: Icon, label, danger, testid, onClick }) => (
    <button
      data-testid={testid}
      onClick={() => { hapticSelect(); onClick(); }}
      className={`w-full flex items-center gap-3.5 px-5 py-3.5 text-[15px] font-medium text-left hover:bg-muted active:bg-muted transition-colors ${danger ? "text-destructive" : ""}`}
    >
      <Icon className="h-5 w-5 shrink-0" /> {label}
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[250]" data-testid="message-actions-sheet">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        data-testid="sheet-backdrop"
        onClick={onClose}
      />
      <div className="absolute inset-x-0 bottom-0 sm:max-w-md sm:mx-auto bg-background rounded-t-2xl border shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-4 fade-in duration-200">
        <div className="flex items-center justify-between pt-2.5 px-3">
          <div className="w-8" />
          <div className="h-1.5 w-10 rounded-full bg-muted" />
          <button
            data-testid="sheet-close"
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        {onReact && (
          <div className="flex justify-center gap-2 py-2.5 border-b" data-testid="sheet-reactions-row">
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                data-testid={`sheet-react-${emoji}`}
                onClick={() => { hapticSelect(); onReact(emoji); onClose(); }}
                className="h-11 w-11 text-2xl rounded-full hover:bg-muted active:scale-95 flex items-center justify-center transition-transform"
              >
                {emoji}
              </button>
            ))}
            {onMoreEmoji && (
              <button
                data-testid="sheet-react-more"
                onClick={() => { hapticSelect(); onClose(); onMoreEmoji(); }}
                className="h-11 w-11 rounded-full bg-muted hover:bg-muted/80 active:scale-95 flex items-center justify-center text-muted-foreground"
                aria-label="+"
              >
                <SmilePlus className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div className="py-1">
          {onReply && <Row icon={Reply} label={t("reply")} testid="sheet-action-reply" onClick={() => { onReply(); onClose(); }} />}
          {onInfo && <Row icon={Info} label={t("message_info")} testid="sheet-action-info" onClick={() => { onInfo(); onClose(); }} />}
          {onEdit && <Row icon={Pencil} label={t("edit_message")} testid="sheet-action-edit" onClick={() => { onEdit(); onClose(); }} />}
          {canCopy && <Row icon={Copy} label={t("copy")} testid="sheet-action-copy" onClick={() => { onCopy(); onClose(); }} />}
          {onStar && (
            <Row
              icon={isStarred ? StarOff : Star}
              label={isStarred ? t("remove_favorite") : t("add_favorite")}
              testid="sheet-action-star"
              onClick={() => { onStar(); onClose(); }}
            />
          )}
          {onPin && <Row icon={Pin} label={t("pin_message")} testid="sheet-action-pin" onClick={() => { onPin(); onClose(); }} />}
          {onLinkJob && <Row icon={Link2} label={linkLabel} testid="sheet-action-link-job" onClick={() => { onLinkJob(); onClose(); }} />}
          {onUnlink && <Row icon={Unlink} label={unlinkLabel} testid="sheet-action-unlink-job" onClick={() => { onUnlink(); onClose(); }} />}
          {onSelect && <Row icon={CheckSquare} label={t("select_items")} testid="sheet-action-select" onClick={() => { onSelect(); onClose(); }} />}
          {canSave && <Row icon={Download} label={saveLabel || t("save_file")} testid="sheet-action-save" onClick={() => { onSave(); onClose(); }} />}
          {onMakeSticker && <Row icon={Sticker} label={t("make_sticker")} testid="sheet-action-make-sticker" onClick={() => { onMakeSticker(); onClose(); }} />}
          <Row icon={Forward} label={t("forward")} testid="sheet-action-forward" onClick={() => { onForward(); onClose(); }} />
          {canDelete && (
            <>
              <div className="border-t my-1" />
              <Row danger icon={Trash2} label={t("delete_message")} testid="sheet-action-delete" onClick={() => { onDelete(); onClose(); }} />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
